import { Button, Divider, Grid, Stack, Typography } from '@mui/material'
import Box from '@mui/material/Box'
import EngineerProjectsListComponent from '../components/Engineering/ProjectsListComponent'
import ArchitectProjectsListComponent from '../components/Architecture/ProjectsListComponent'
import { UserRoleNames } from '../models/AccessControl'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import { useCallback, useEffect, useRef, useState } from 'react'
import StartNewProjectComponent from '../components/Architecture/StartNewProjectComponent'
import RecentProjectsComponent from '../components/Architecture/RecentProjectsComponent'
import { SortIcon } from '../components/Svgs'
import RecentProjectsLinkComponent from '../components/Architecture/RecentProjectsLinkComponent'
import { createSelector } from '@reduxjs/toolkit'
import HistoryHandlersComponent from '../HistoryHandlersComponent'
import { useParams } from 'react-router-dom'

const selectedClientSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.selectedClient,
)

function Projects() {
  const gridRef = useRef<HTMLDivElement>(null)
  const [gridHeight, setGridHeight] = useState(0)

  const { clientId } = useParams<{ clientId: string }>()
  const user = useSelector((state: RootState) => state.auth.user)
  const selectedClient = useSelector(selectedClientSelector)

  useEffect(() => {
    const calculateHeight = () => {
      if (gridRef.current) {
        setGridHeight(gridRef.current.clientHeight)
      }
    }

    calculateHeight()
    window.addEventListener('resize', calculateHeight)

    return () => {
      window.removeEventListener('resize', calculateHeight)
    }
  }, [])

  const renderArchitectPage = () => {
    return (
      <Box
        gap={'2.5rem'}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Grid ref={gridRef} container spacing='1rem'>
          <Grid size={3}>
            <Stack gap='1rem'>
              <Box display='flex' width='100%'>
                <Typography
                  sx={{
                    color: '#06004A',
                    fontSize: '0.94rem',
                    fontWeight: 500,
                    lineHeight: 'normal',
                  }}
                >
                  Start a project
                </Typography>
              </Box>
              <Box sx={{ height: '5.5rem' }}>
                <StartNewProjectComponent />
              </Box>
            </Stack>
          </Grid>
          <Grid size={9}>
            <Stack gap='1rem'>
              <Box
                display='flex'
                justifyContent='space-between'
                alignItems='center'
                width='100%'
              >
                <Typography
                  sx={{
                    color: '#06004A',
                    fontSize: '0.94rem',
                    fontWeight: 500,
                    lineHeight: 'normal',
                  }}
                >
                  Recent Projects
                </Typography>
                <RecentProjectsLinkComponent />
              </Box>
              <RecentProjectsComponent />
            </Stack>
          </Grid>
        </Grid>
        <Stack spacing='1rem' height={`calc(100% - ${gridHeight}px - 2.5rem)`}>
          {!!clientId ? (
            <Stack spacing='1rem'>
              <HistoryHandlersComponent />
              <Box
                display='flex'
                width='100%'
                gap='2rem'
                alignItems='center'
                justifyContent='space-between'
              >
                <Typography
                  sx={{
                    color: '#06004A',
                    fontSize: '0.94rem',
                    fontWeight: 500,
                    lineHeight: 'normal',
                  }}
                >
                  {selectedClient?.name}
                </Typography>
                <Box display='flex' gap='1rem'>
                  <Box display='flex' gap='0.25rem'>
                    <SortIcon color='#1705E5' />
                    <Typography
                      sx={{
                        color: '#797979',
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        lineHeight: 'normal',
                      }}
                    >
                      Sort
                    </Typography>
                  </Box>
                  <Divider flexItem orientation='vertical' />
                  <Typography
                    sx={{
                      color: '#797979',
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      lineHeight: 'normal',
                    }}
                  >
                    ProjectStatus
                  </Typography>
                </Box>
              </Box>
            </Stack>
          ) : (
            <Box display='flex' width='100%' gap='2rem' alignItems='center'>
              <Typography
                sx={{
                  color: '#06004A',
                  fontSize: '0.94rem',
                  fontWeight: 500,
                  lineHeight: 'normal',
                }}
              >
                All
              </Typography>
              <Box display='flex' gap='1rem'>
                <Box display='flex' gap='0.25rem'>
                  <SortIcon color='#1705E5' />
                  <Typography
                    sx={{
                      color: '#797979',
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      lineHeight: 'normal',
                    }}
                  >
                    Sort
                  </Typography>
                </Box>
                <Divider flexItem orientation='vertical' />
                <Typography
                  sx={{
                    color: '#797979',
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    lineHeight: 'normal',
                  }}
                >
                  ProjectStatus
                </Typography>
              </Box>
            </Box>
          )}
          <ArchitectProjectsListComponent clientId={selectedClient?.id} />
        </Stack>
      </Box>
    )
  }

  const renderEngineerPage = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box display='flex' justifyContent='space-between'>
          <Typography
            sx={{
              color: '#06004A',
              fontSize: '0.94rem',
              fontWeight: 500,
              lineHeight: 'normal',
            }}
          >
            Projects
          </Typography>
          <Button
            disableRipple
            variant='text'
            sx={{
              color: '#06004A',
              fontSize: '0.76rem',
              fontWeight: 500,
              lineHeight: 'normal',
              textTransform: 'capitalize',
            }}
          >
            View All
          </Button>
        </Box>
        <EngineerProjectsListComponent />
      </Box>
    )
  }

  const renderProjectsPage = useCallback(() => {
    switch (user?.role.name) {
      case UserRoleNames.Admin:
      case UserRoleNames.LeadingArchitect:
      case UserRoleNames.Architect:
        return renderArchitectPage()
      case UserRoleNames.EngineeringAdmin:
      case UserRoleNames.LeadingStructuralEngineer:
      case UserRoleNames.LeadingElectricalEngineer:
      case UserRoleNames.StructuralEngineer:
      case UserRoleNames.ElectricalEngineer:
        return renderEngineerPage()
      default:
        return <Box />
    }
  }, [user, selectedClient, gridHeight])

  return <>{!!user && renderProjectsPage()}</>
}

export default Projects
